import { useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useParams } from "react-router-dom";
import Avatar from "../components/Avatar";
import { PlanPill, StatePill, TagChip } from "../components/Pills";
import { ErrorBanner, LoadingNote } from "../components/Layout";
import { useDashboardData } from "../hooks/useDashboardData";
import { fetchCustomer } from "../dashboardApi";
import { CustomerDetail } from "../types";
import { colors, type } from "../styles";
import { dashboardPath } from "../routes";
import RevenueSection from "./customers/RevenueSection";
import TextingSection from "./customers/TextingSection";
import AccountSection from "./customers/AccountSection";

const ActionLink = ({
  label,
  icon,
  href,
  onClick,
}: {
  label: string;
  icon: string;
  href?: string;
  onClick?: () => void;
}) => {
  const style = {
    ...type.bodySmallMedium,
    color: colors.inkMuted,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    borderRadius: 6,
    padding: "6px 11px",
    cursor: "pointer",
  } as const;

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={style}>
        <Icon icon={icon} height={14} />
        {label}
      </a>
    );
  }
  return (
    <button onClick={onClick} style={{ ...style, font: "inherit" }}>
      <Icon icon={icon} height={14} />
      {label}
    </button>
  );
};

const CustomerDetailScreen = () => {
  const { uid = "" } = useParams();
  const [copied, setCopied] = useState(false);

  const fetcher = useCallback(() => fetchCustomer(uid), [uid]);
  const { data, error, loading } = useDashboardData<CustomerDetail>(fetcher, [uid]);

  const copySid = async (sid: string) => {
    try {
      await navigator.clipboard.writeText(sid);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access can be denied; the SID is not shown inline anywhere
      // else, so say nothing rather than pretending the copy worked.
      setCopied(false);
    }
  };

  return (
    <div>
      <Link
        to={dashboardPath.customers()}
        style={{
          ...type.bodySmallMedium,
          color: colors.inkMuted,
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          marginBottom: 16,
        }}
      >
        <Icon icon="ion:chevron-back" height={13} />
        All customers
      </Link>

      {error && <ErrorBanner message={error} />}
      {loading && <LoadingNote />}

      {data && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 16,
              marginBottom: 28,
              flexWrap: "wrap",
            }}
          >
            <Avatar
              url={data.profilePicURL}
              fullName={data.fullName}
              username={data.username}
              size={56}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0, flex: 1 }}>
              <div style={{ ...type.pageTitle, color: colors.ink }}>{data.fullName}</div>
              <div style={{ ...type.bodyRegular, color: colors.inkMuted }}>
                @{data.username}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <PlanPill plan={data.plan} />
                <StatePill state={data.subscriptionState} />
                {data.cancelAtEnd && (
                  <TagChip label="Canceling at period end" tone="warning" />
                )}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                {data.username && (
                  <ActionLink
                    label="View profile"
                    icon="ion:person-circle-outline"
                    href={`/u/${data.username}`}
                  />
                )}
                {data.stripeCustomerId && (
                  <ActionLink
                    label="Stripe customer"
                    icon="ion:card-outline"
                    href={`https://dashboard.stripe.com/customers/${data.stripeCustomerId}`}
                  />
                )}
                {data.stripeAccountId && (
                  <ActionLink
                    label="Stripe Connect account"
                    icon="ion:business-outline"
                    href={`https://dashboard.stripe.com/connect/accounts/${data.stripeAccountId}`}
                  />
                )}
                {data.twilio.subAccountSid && (
                  <ActionLink
                    label={copied ? "Copied" : "Copy Twilio sub-account SID"}
                    icon={copied ? "ion:checkmark" : "ion:copy-outline"}
                    onClick={() => copySid(data.twilio.subAccountSid)}
                  />
                )}
              </div>
            </div>
          </div>

          <RevenueSection customer={data} />
          <TextingSection uid={uid} />
          <AccountSection customer={data} />
        </>
      )}
    </div>
  );
};

export default CustomerDetailScreen;
